import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { Platform, View, StyleSheet } from 'react-native';
import { useThemeName, Text } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList, TabParamList } from '@/types/navigation';
import { Header, Screen } from '@/components';
import SearchBar from './components/SearchBar';
import StoriesSection from './components/StoriesSection';
import MessagesList from './components/MessagesList';
import ChatConversationScreen from '@/screens/ChatConversationScreen';
import { useChats, useMarkAsRead } from '@/hooks/useFirebaseChat';
import { COLORS } from '@/constants/colors';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import { useActiveChat } from '@/contexts/ActiveChatContext';

const BOTTOM_PADDING = 90;

const ChatScreen = React.memo(() => {
  const isMobileLayout = useIsMobileLayout();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const { setActiveChatId } = useActiveChat();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<TabParamList, 'Chat'>>();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;

  // Pobierz parametry nawigacji (opcjonalne)
  const initialChatId = route.params?.initialChatId;

  // Use Firebase for chats
  const { chats, isLoading, error, refetch } = useChats();
  const { markAsRead } = useMarkAsRead();

  // Refetch chats when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Ustaw początkowy czat jeśli przekazano przez nawigację
  useEffect(() => {
    if (initialChatId && !selectedChatId) {
      setSelectedChatId(initialChatId);
    }
  }, [initialChatId, selectedChatId]);

  // Ref do śledzenia poprzedniego czatu (dla oznaczania jako przeczytane przy zmianie)
  const prevSelectedChatIdRef = useRef<string | null>(null);

  // Synchronizuj selectedChatId z globalnym activeChatId (dla pomijania dźwięku)
  // oraz oznacz wiadomości jako przeczytane przy wejściu/wyjściu z czatu
  useEffect(() => {
    if (Platform.OS === 'web' && !isMobileLayout) {
      // Oznacz poprzedni czat jako przeczytany przy zmianie, potem ustaw nowy
      const setupNewChat = async () => {
        if (prevSelectedChatIdRef.current && prevSelectedChatIdRef.current !== selectedChatId) {
          await markAsRead(prevSelectedChatIdRef.current);
        }
        
        setActiveChatId(selectedChatId);
        // Oznacz wiadomości jako przeczytane gdy czat zostanie wybrany
        if (selectedChatId) {
          markAsRead(selectedChatId);
        }
        
        prevSelectedChatIdRef.current = selectedChatId;
      };
      
      setupNewChat();
    }
    return () => {
      if (Platform.OS === 'web' && !isMobileLayout) {
        // Oznacz jako przeczytane przy odmontowaniu, potem wyczyść
        if (prevSelectedChatIdRef.current) {
          markAsRead(prevSelectedChatIdRef.current).then(() => {
            setActiveChatId(null);
          });
        } else {
          setActiveChatId(null);
        }
      }
    };
  }, [selectedChatId, isMobileLayout, setActiveChatId, markAsRead]);

  // Filtrowanie czatów po wyszukiwaniu
  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return chats;
    const query = searchQuery.toLowerCase();
    return chats.filter(
      (chat) =>
        chat.odataName.toLowerCase().includes(query) ||
        chat.lastMessage.toLowerCase().includes(query)
    );
  }, [chats, searchQuery]);

  const handleWalletPress = useCallback(() => {
    navigation.navigate('Wallet');
  }, [navigation]);

  const handleFilterPress = useCallback(() => {
    // TODO: Implement filter functionality
  }, []);

  const handleStoryPress = useCallback((userId: string) => {
    // TODO: Navigate to story view
    console.log('Story pressed:', userId);
  }, []);

  const handleChatPress = useCallback((chatId: string) => {
    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      if (Platform.OS === 'web' && !isMobileLayout) {
        // On web with desktop layout, set selected chat for split view
        setSelectedChatId(chat.id);
      } else {
        // On mobile or web with mobile layout, navigate to conversation screen
        navigation.navigate('ChatConversation', {
          chatId: chat.id,
          name: chat.odataName,
          imageUri: chat.odataAvatar,
          meetingInfo: chat.meetingInfo,
        });
      }
    }
  }, [chats, navigation, isMobileLayout]);

  const selectedChat = useMemo(() => {
    return chats.find((c) => c.id === selectedChatId);
  }, [chats, selectedChatId]);

  // Web/Tablet layout - split view (only if width >= 800px)
  if (Platform.OS === 'web' && !isMobileLayout) {
    return (
      <Screen>
        <XStack flex={1} style={webStyles.container}>
          {/* Left column - Chat List */}
          <View style={webStyles.leftColumn}>
            <Header onPressLeft={handleWalletPress} onPressRight={handleFilterPress} />
            <YStack flex={1} paddingBottom={BOTTOM_PADDING}>
              {/* Search Bar */}
              <YStack paddingHorizontal="$4" paddingTop="$2" paddingBottom="$3">
                <SearchBar
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  isDark={isDark}
                />
              </YStack>

              {/* Stories Section - temporarily hidden */}
              {/* {chats.length > 0 && (
                <StoriesSection
                  textColor={textColor}
                  isDark={isDark}
                  onStoryPress={handleStoryPress}
                />
              )} */}

              {/* Messages List */}
              <MessagesList
                textColor={textColor}
                isDark={isDark}
                onChatPress={handleChatPress}
                chats={filteredChats}
                isLoading={isLoading}
                error={error}
                selectedChatId={selectedChatId}
              />
            </YStack>
          </View>

          {/* Right column - Messages */}
          <View style={webStyles.rightColumn}>
            {selectedChat ? (
              <ChatConversationScreen
                route={{
                  params: {
                    chatId: selectedChat.id,
                    name: selectedChat.odataName,
                    imageUri: selectedChat.odataAvatar,
                    meetingInfo: selectedChat.meetingInfo,
                  },
                } as any}
                navigation={navigation as any}
                {...({} as any)}
              />
            ) : (
              <YStack flex={1} alignItems="center" justifyContent="center" backgroundColor={isDark ? COLORS.BLACK : COLORS.WHITE}>
                <YStack alignItems="center" gap="$3" padding="$4">
                  <Text fontSize={18} fontWeight="600" color={isDark ? COLORS.WHITE : COLORS.DARK_TEXT}>
                    Wybierz rozmowę
                  </Text>
                  <Text fontSize={14} color={isDark ? COLORS.WHITE : COLORS.DARK_TEXT} opacity={0.6} textAlign="center">
                    Wybierz rozmowę z listy, aby zobaczyć wiadomości
                  </Text>
                </YStack>
              </YStack>
            )}
          </View>
        </XStack>
      </Screen>
    );
  }

  // Mobile layout - single column
  return (
    <Screen>
      <Header onPressLeft={handleWalletPress} onPressRight={handleFilterPress} />
      
      <YStack flex={1} paddingBottom={BOTTOM_PADDING}>
        {/* Search Bar */}
        <YStack paddingHorizontal="$4" paddingTop="$2" paddingBottom="$3">
          <SearchBar
            value={searchQuery}
            onChangeText={setSearchQuery}
            isDark={isDark}
          />
        </YStack>

        {/* Stories Section - temporarily hidden */}
        {/* {chats.length > 0 && (
          <StoriesSection
            textColor={textColor}
            isDark={isDark}
            onStoryPress={handleStoryPress}
          />
        )} */}

        {/* Messages List */}
        <MessagesList
          textColor={textColor}
          isDark={isDark}
          onChatPress={handleChatPress}
          chats={filteredChats}
          isLoading={isLoading}
          error={error}
        />
      </YStack>
    </Screen>
  );
});

const webStyles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  leftColumn: {
    width: '40%',
    minWidth: 300,
    borderRightWidth: 1,
    borderRightColor: 'rgba(0,0,0,0.1)',
  },
  rightColumn: {
    flex: 1,
    minWidth: 400,
  },
});

ChatScreen.displayName = 'ChatScreen';

export default ChatScreen;

